// Backline image uploader: lets a musician/crew backline row carry a reference
// photo (data URL, stored in the rider JSON) so the exact model shows in the
// rider PDF. Fallbacks gracefully when the browser cannot read the file.

import { useRef } from 'react';
import { apiUploadImage } from '../api';

interface BacklineImageUploaderProps {
  imageUrl?: string;
  onChange: (imageUrl: string | undefined) => void;
  size?: number;
  /** Freeze the uploader (lot A2-CAP). Defaults to false. */
  disabled?: boolean;
}

export default function BacklineImageUploader({
  imageUrl,
  onChange,
  size = 56,
  disabled = false,
}: BacklineImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please choose an image file (PNG, JPG, WebP…).');
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = String(reader.result);
      try {
        // Prefer server-side storage: the rider JSON stays small.
        const { url } = await apiUploadImage(dataUrl);
        onChange(url);
      } catch (err) {
        console.warn('[BacklineImageUploader] upload failed, keeping local data URL:', err);
        // Offline fallback: keep the image as a data URL so nothing is lost.
        onChange(dataUrl);
      }
    };
    reader.onerror = () => {
      alert('Could not read that image. Try another file.');
    };
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt="Backline reference"
          style={{
            width: size,
            height: size,
            objectFit: 'contain',
            borderRadius: 6,
            border: '1px solid var(--border)',
            background: 'var(--surface)',
          }}
        />
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          title="Add a reference image"
          disabled={disabled}
          style={{
            width: size,
            height: size,
            borderRadius: 6,
            border: '1px dashed var(--border)',
            background: 'transparent',
            cursor: 'pointer',
            color: 'var(--text-dim)',
            fontSize: '1.2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          🖼
        </button>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          disabled={disabled}
          style={{ display: 'none' }}
          onChange={(e) => {
            handleFile(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
        {imageUrl && (
          <>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={disabled}
              style={{ fontSize: '0.7rem', background: 'none', border: 'none', color: 'var(--text-dim)', cursor: disabled ? 'default' : 'pointer', padding: 0, textAlign: 'left' }}
            >
              Replace
            </button>
            <button
              type="button"
              onClick={() => onChange(undefined)}
              disabled={disabled}
              style={{ fontSize: '0.7rem', background: 'none', border: 'none', color: 'var(--danger)', cursor: disabled ? 'default' : 'pointer', padding: 0, textAlign: 'left' }}
            >
              Remove
            </button>
          </>
        )}
      </div>
    </div>
  );
}
